import { Injectable } from '@angular/core';
import { AppConfigurationService } from '../appconfiguration/appconfiguration.service';
import { FeatureFlagsServiceBase } from './feature-flags-base.service';

export enum FeatureFlag {
  DemoPreviews = 'demoPreviews',
  DevTools = 'devTools',
  DevExceptions = 'devExceptions',
  EnableMatterSubtypePicklistSettingsEditing = 'enableMatterSubtypePicklistSettingsEditing',
  ShowMatterSubtypePicklistSettings = 'showMatterSubtypePicklistSettings',
  ShowCustomPicklistSettings = 'showCustomPicklistSettings',
  EnableCustomPicklistSettingsEditing = 'enableCustomPicklistSettingsEditing',
  InputRelativeValidation = 'inputRelativeValidation',
  ShowServiceAccountSettings = 'showServiceAccountSettings',
  NotificationBellEnabled = 'notificationBellEnabled',
  EnableCloneMatter = 'enableCloneMatter',
  EnableServeportPortalEdit = 'enableServeportPortalEdit',
  EnableServiceOfProcessBusinessHours = 'enableServiceOfProcessBusinessHours',
  EnableBlackMamba = 'enableBlackMamba',
  EnableFederalBenefitExemptionStatus = 'enableFederalBenefitExemptionStatus',
  EnableEmailFileViewer = 'enableEmailFileViewer',
  EnableNonPdfIntakeUpload = 'enableNonPdfIntakeUpload',
  EnablePdfTemplates = 'enablePdfTemplates',
  EnableTaskGroups = 'enableTaskGroups'
}

/** Dev-only flags never come from App Configuration. */
const STATIC_ONLY_FEATURE_FLAGS: readonly FeatureFlag[] = [FeatureFlag.DemoPreviews, FeatureFlag.DevTools, FeatureFlag.DevExceptions];

/** Flags App Configuration can set per user and company, which LPMS asks CoManage about. */
export const RELEASE_FEATURE_FLAGS: readonly FeatureFlag[] = Object.values(FeatureFlag).filter(flag => !STATIC_ONLY_FEATURE_FLAGS.includes(flag));

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService extends FeatureFlagsServiceBase<FeatureFlag> {
  constructor(appConfig: AppConfigurationService) {
    super(appConfig);
  }

  get demoPreviews() {
    return this.isFeatureFlagEnabled(FeatureFlag.DemoPreviews);
  }
  get devTools() {
    return this.isFeatureFlagEnabled(FeatureFlag.DevTools);
  }
  get devExceptions() {
    return this.isFeatureFlagEnabled(FeatureFlag.DevExceptions);
  }
  get enableMatterSubtypePicklistSettingsEditing() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableMatterSubtypePicklistSettingsEditing);
  }
  get showMatterSubtypePicklistSettings() {
    return this.isFeatureFlagEnabled(FeatureFlag.ShowMatterSubtypePicklistSettings);
  }
  get showCustomPicklistSettings() {
    return this.isFeatureFlagEnabled(FeatureFlag.ShowCustomPicklistSettings);
  }
  get enableCustomPicklistSettingsEditing() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableCustomPicklistSettingsEditing);
  }
  get inputRelativeValidation() {
    return this.isFeatureFlagEnabled(FeatureFlag.InputRelativeValidation);
  }
  get showServiceAccountSettings() {
    return this.isFeatureFlagEnabled(FeatureFlag.ShowServiceAccountSettings);
  }
  get notificationBellEnabled() {
    return this.isFeatureFlagEnabled(FeatureFlag.NotificationBellEnabled);
  }
  get enableCloneMatter() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableCloneMatter);
  }
  get enableServeportPortalEdit() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableServeportPortalEdit);
  }
  get enableServiceOfProcessBusinessHours() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableServiceOfProcessBusinessHours);
  }
  get enableBlackMamba() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableBlackMamba);
  }
  get enableFederalBenefitExemptionStatus() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableFederalBenefitExemptionStatus);
  }
  get enableEmailFileViewer() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableEmailFileViewer);
  }
  get enableNonPdfIntakeUpload() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableNonPdfIntakeUpload);
  }
  get enablePdfTemplates() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnablePdfTemplates);
  }
  get enableTaskGroups() {
    return this.isFeatureFlagEnabled(FeatureFlag.EnableTaskGroups);
  }
}
